/* eslint-disable react-refresh/only-export-components -- shared streaming-status helpers are intentionally colocated with the component. */
import { useEffect, useState } from 'preact/hooks'
import type { WebsterChatMessage } from '@/types/webster'

/**
 * Maps SignalR event types to human-readable state labels for the streaming
 * indicator's "(15s · thinking)" suffix. Re-export so TypingIndicator (in
 * WebsterChatArea) can mirror the vocabulary when it falls back to a generic
 * "thinking" state without an active streamStatus.
 */
export const STATE_LABEL_BY_TYPE: Record<string, string> = {
    thinking: 'thinking',
    analyzing: 'analyzing',
    tool_use: 'researching',
    content: 'writing',
    rebuilding: 'rebuilding',
    editing: 'applying changes',
    done: 'finishing',
}

/** "thinking" → "still thinking" past 12s, "really thinking" past 30s. */
export function intensifyLabel(base: string, elapsedSeconds: number): string {
    if (elapsedSeconds > 30) return `really ${base}`
    if (elapsedSeconds > 12) return `still ${base}`
    return base
}

/**
 * Humanize the elapsed duration for the streaming indicator. Keeps the suffix
 * readable when generation runs long: "59 seconds", "1 minute", "1 minute 1
 * second", "5 minutes 40 seconds" — never a bare "340s".
 *
 * Floors at 1, so the counter opens on "1 second" rather than "0 seconds": the
 * indicator mounts before the first tick, and a timer that starts at zero reads
 * as stopped.
 */
export function formatElapsed(totalSeconds: number): string {
    const total = Math.max(1, Math.floor(totalSeconds))
    const minutes = Math.floor(total / 60)
    const seconds = total % 60
    const parts: string[] = []
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`)
    if (seconds > 0 || minutes === 0) parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`)
    return parts.join(' ')
}

interface StreamingStatusLineProps {
    status: NonNullable<WebsterChatMessage['streamStatus']>
}

/**
 * The animated streaming indicator that lives under the Webster avatar while
 * a generation is in flight. Renders the rotating status phrase, elapsed time,
 * and intensified state label — "(15s · thinking)" / "(45s · still thinking)".
 *
 * The per-message token count surfaces as a footer on the finalized Webster
 * bubble after the turn completes (see MessageTokenFooter), not as a live
 * ticker on the streaming line. That keeps the in-flight indicator focused
 * on "Webster is still working" — elapsed time is the reassurance signal,
 * tokens are the after-the-fact accounting.
 */
export function StreamingStatusLine({ status }: StreamingStatusLineProps) {
    // Tick once per second so the elapsed-time counter and state-label intensifier
    // stay current. Interval is cheap and only runs while a streaming bubble is
    // mounted — it tears down as soon as the message finalizes.
    const [, setTick] = useState(0)
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 1000)
        return () => clearInterval(id)
    }, [])

    const startedAt = status.startedAt
    const elapsedSeconds = startedAt ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0
    const baseLabel = STATE_LABEL_BY_TYPE[status.type] ?? 'thinking'
    const label = intensifyLabel(baseLabel, elapsedSeconds)

    return (
        <span className="text-sm text-slate-500 dark:text-slate-400 italic animate-pulse">
            {status.text}
            {startedAt ? (
                <span className="not-italic text-xs text-slate-400 dark:text-slate-500 ml-1.5">
                    ({formatElapsed(elapsedSeconds)} · {label})
                </span>
            ) : null}
        </span>
    )
}
